/* ============================================================================
   Trainer CSV parser
   ----------------------------------------------------------------------------
   Maps an inventory CSV (from any tracker the user already uses, or their own
   offline spreadsheet matching the Nexus Dex template) to the lake/user-view
   ParsedRow shape.

   Privacy contract, raw lat/long is bucketed via toGeoBucketL17 and then
   IMMEDIATELY DROPPED from the row before it leaves the browser. The server
   only ever sees the ~80m S2 cell token (or null).

   Expected columns (header: true):
     Index, Name, Form, Pokemon Number, Gender, CP, HP,
     Atk IV, Def IV, Sta IV, IV Avg, Level Min, Level Max,
     Quick Move, Charge Move 1, Charge Move 2, Catch Date,
     Weight, Height, Lucky, Shiny, Shadow/Purified, Form,
     Has 3rd Move, Lat, Lng, Catch Place
   ============================================================================ */
import Papa from "papaparse";
import { toGeoBucketL17 } from "./geoBucket";

export interface ParsedRow {
  speciesId: number;
  formId: string | null;
  isShiny: boolean;
  isLucky: boolean;
  isShadow: boolean;
  isPurified: boolean;
  ivAtk: number | null;
  ivDef: number | null;
  ivSta: number | null;
  cp: number | null;
  hp: number | null;
  level: number | null;
  caughtAt: string | null;        // ISO string
  geoS2L17: string | null;
  nickname: string | null;
  metaJson: Record<string, unknown> | null;
}

export interface ParseResult {
  rows: ParsedRow[];
  errors: { rowIndex: number; reason: string }[];
}

function intOrNull(v: unknown): number | null {
  if (v === undefined || v === null || v === "") return null;
  const n = parseInt(String(v).trim(), 10);
  return Number.isFinite(n) ? n : null;
}

function floatOrNull(v: unknown): number | null {
  if (v === undefined || v === null || v === "") return null;
  const n = parseFloat(String(v).trim());
  return Number.isFinite(n) ? n : null;
}

function boolFromCell(v: unknown): boolean {
  if (v === undefined || v === null) return false;
  const s = String(v).trim().toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "y";
}

function parseCatchDate(v: unknown): string | null {
  if (!v) return null;
  const s = String(v).trim();
  if (!s) return null;
  // CSVs typically use YYYY-MM-DD HH:mm:ss. Try that first, fallback to Date parse.
  const direct = new Date(s.replace(" ", "T"));
  if (!Number.isNaN(direct.getTime())) return direct.toISOString();
  const fallback = new Date(s);
  if (!Number.isNaN(fallback.getTime())) return fallback.toISOString();
  return null;
}

function detectShadowPurified(cell: unknown): { isShadow: boolean; isPurified: boolean } {
  if (cell === undefined || cell === null) return { isShadow: false, isPurified: false };
  const s = String(cell).trim().toLowerCase();
  if (s.includes("shadow")) return { isShadow: true, isPurified: false };
  if (s.includes("purif")) return { isShadow: false, isPurified: true };
  return { isShadow: false, isPurified: false };
}

/**
 * Parse a Trainer CSV (string or File-readable text) into ParsedRow[].
 * Returns rows + errors. Rows that fail the species-id check are dropped
 * and recorded in errors, the rest are kept (we degrade gracefully on
 * missing IVs etc.).
 */
export function parseTrainerCsv(csvText: string): ParseResult {
  const result = Papa.parse<Record<string, string>>(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h: string) => h.trim(),
  });

  const errors: ParseResult["errors"] = [];
  const rows: ParsedRow[] = [];

  result.data.forEach((raw, i) => {
    if (!raw || typeof raw !== "object") {
      errors.push({ rowIndex: i, reason: "Row is empty or malformed" });
      return;
    }

    const speciesId = intOrNull(raw["Pokemon Number"] ?? raw["Pokémon Number"]);
    if (speciesId === null) {
      errors.push({ rowIndex: i, reason: "Missing Pokémon Number" });
      return;
    }

    const formCell = (raw["Form"] ?? "").trim();
    const formId = formCell && formCell.toLowerCase() !== "normal" ? formCell : null;

    const ivAtk = intOrNull(raw["Atk IV"]);
    const ivDef = intOrNull(raw["Def IV"]);
    const ivSta = intOrNull(raw["Sta IV"]);
    const cp = intOrNull(raw["CP"]);
    const hp = intOrNull(raw["HP"]);

    // Use Level Min as a starting estimate per spec.
    const level = floatOrNull(raw["Level Min"]);

    const isShiny = boolFromCell(raw["Shiny"]);
    const isLucky = boolFromCell(raw["Lucky"]);
    const { isShadow, isPurified } = detectShadowPurified(raw["Shadow/Purified"]);

    const caughtAt = parseCatchDate(raw["Catch Date"]);

    // Geo bucketing, raw lat/lng is consumed locally and then dropped.
    const lat = floatOrNull(raw["Lat"]);
    const lng = floatOrNull(raw["Lng"]);
    const geoS2L17 = toGeoBucketL17(lat ?? undefined, lng ?? undefined);

    const nickname = (raw["Name"] ?? "").trim() || null;

    const metaJson: Record<string, unknown> = {};
    if (raw["Index"]) metaJson.csvIndex = raw["Index"];
    if (raw["Gender"]) metaJson.gender = raw["Gender"];
    if (raw["IV Avg"]) metaJson.ivAvg = raw["IV Avg"];
    if (raw["Level Max"]) metaJson.levelMax = raw["Level Max"];
    if (raw["Quick Move"]) metaJson.quickMove = raw["Quick Move"];
    if (raw["Charge Move 1"]) metaJson.chargeMove1 = raw["Charge Move 1"];
    if (raw["Charge Move 2"]) metaJson.chargeMove2 = raw["Charge Move 2"];
    if (raw["Weight"]) metaJson.weight = raw["Weight"];
    if (raw["Height"]) metaJson.height = raw["Height"];
    if (raw["Has 3rd Move"]) metaJson.has3rdMove = raw["Has 3rd Move"];
    if (raw["Catch Place"]) metaJson.catchPlace = raw["Catch Place"];

    rows.push({
      speciesId,
      formId,
      isShiny,
      isLucky,
      isShadow,
      isPurified,
      ivAtk,
      ivDef,
      ivSta,
      cp,
      hp,
      level,
      caughtAt,
      geoS2L17,
      nickname,
      metaJson: Object.keys(metaJson).length > 0 ? metaJson : null,
    });
  });

  if (result.errors && result.errors.length > 0) {
    for (const e of result.errors) {
      errors.push({ rowIndex: e.row ?? -1, reason: e.message });
    }
  }

  return { rows, errors };
}

/**
 * Compute SHA-256 of a File's raw bytes via crypto.subtle.
 * Returns a lowercase hex string.
 */
export async function sha256OfFile(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buf);
  const bytes = new Uint8Array(hash);
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, "0");
  }
  return out;
}
